import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface RepairFeature {
  point: string;
  detail: string;
}

export interface RepairTab {
  key: string;
  label: string;
  image: string;
  objectPosition?: string;
  objectFit?: 'cover' | 'contain';
  imageBg?: string;
  priceFrom: string;
  time: string;
  warranty: string;
  features: RepairFeature[];
}

interface Props {
  tabs: RepairTab[];
  note?: string;
  noteType?: 'info' | 'warning';
}

export default function RepairTabs({ tabs, note, noteType = 'info' }: Props) {
  const [active, setActive] = useState(tabs[0]?.key ?? '');
  const navigate = useNavigate();

  const current = tabs.find(t => t.key === active) ?? tabs[0];
  if (!current) return null;

  return (
    <div>
      {/* Tab Bar */}
      <div className="flex gap-1 flex-wrap border-b border-gray-150 mb-8">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            className={`px-5 py-3 text-sm font-medium cursor-pointer whitespace-nowrap transition-all relative ${
              active === tab.key
                ? 'text-orange-500'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
            {active === tab.key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        {/* Left: Image */}
        <div className={`relative overflow-hidden rounded-2xl ${current.imageBg ?? ''}`}>
          <img
            key={current.key}
            src={current.image}
            alt={current.label}
            className={`w-full h-80 ${current.objectFit === 'contain' ? 'object-contain p-6' : 'object-cover'}`}
            style={{ objectPosition: current.objectFit === 'contain' ? 'center' : (current.objectPosition ?? 'top') }}
          />
          {/* Price badge */}
          <div className="absolute top-4 left-4 bg-gray-900/80 backdrop-blur-sm text-white px-4 py-2 rounded-xl">
            <div className="text-xs text-gray-400 mb-0.5">參考報價</div>
            <div className="text-lg font-bold text-orange-400">{current.priceFrom}</div>
          </div>
        </div>

        {/* Right: Features */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{current.label}</h3>
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-1.5 text-gray-500 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-orange-400" />
              </div>
              維修時間：{current.time}
            </div>
            <div className="flex items-center gap-1.5 text-gray-500 text-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-line text-orange-400" />
              </div>
              保固：{current.warranty}
            </div>
          </div>

          <div className="space-y-4">
            {current.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 bg-orange-100 rounded-full">
                  <i className="ri-check-line text-orange-500 text-xs" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-800">{f.point}</div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.detail}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-8">
            <button
              onClick={() => navigate('/pricing')}
              className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors text-sm"
            >
              查詢精確報價
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="flex-1 py-3 border-2 border-gray-900 hover:bg-gray-900 hover:text-white text-gray-900 font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors text-sm"
            >
              立即預約
            </button>
          </div>
        </div>
      </div>

      {/* Note */}
      {note && (
        <div className={`mt-6 rounded-xl p-4 flex gap-3 ${noteType === 'warning' ? 'bg-amber-50 border border-amber-100' : 'bg-orange-50 border border-orange-100'}`}>
          <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
            <i className={`text-sm ${noteType === 'warning' ? 'ri-error-warning-line text-amber-500' : 'ri-information-line text-orange-400'}`} />
          </div>
          <p className={`text-sm ${noteType === 'warning' ? 'text-amber-700' : 'text-orange-700'}`} dangerouslySetInnerHTML={{ __html: note }} />
        </div>
      )}
    </div>
  );
}
